import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { VchasnoKasaService } from '../../services/vchasno-kasa.service';
import { FiscalHistory } from './prro.types';
import { historyItem, HistoryItem } from './fiscal-history.view';
import { StackedSidebarService } from '../../services/stacked-sidebar.service';
import { SidebarType } from '../layout/stacked-sidebar/stacked-sidebar.interface';

@Component({
  selector: 'app-fiscal-history',
  standalone: false,
  templateUrl: './fiscal-history.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full min-h-0' },
})
export class FiscalHistoryComponent implements OnInit {
  @Input({ required: true }) registerId!: string;
  @Input() registerName = '';
  @Input() shiftId: string | null = null;
  @Output() close = new EventEmitter<void>();
  protected items: HistoryItem[] = [];
  protected expanded = new Set<HistoryItem>();
  private readonly api = inject(VchasnoKasaService);
  private readonly sidebar = inject(StackedSidebarService);
  private readonly destroyRef = inject(DestroyRef);
  protected selectedShift: string | null = null;
  protected history: FiscalHistory | null = null;
  protected page = 1;
  protected loading = false;
  protected error = '';

  ngOnInit(): void {
    this.selectedShift = this.shiftId;
    this.load();
  }

  protected load(step = 0): void {
    if (this.loading) return;
    const page = Math.max(1, this.page + step);
    this.loading = true;
    this.error = '';
    this.api.history(this.registerId, page, this.selectedShift ?? undefined)
      .pipe(finalize(() => (this.loading = false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (history) => {
          this.history = history;
          this.page = page;
          this.items = history.items.map((record) => historyItem(record, !this.selectedShift));
          this.expanded.clear();
        },
        error: (error) => { this.error = error?.status === 403 ? 'prro.denied' : 'prro.load_failed'; },
      });
  }

  protected showChecks(record: Record<string, unknown>): void {
    const id = record['shift_id'] ?? record['id'];
    if (typeof id !== 'string' || this.loading) return;
    this.selectedShift = id;
    this.page = 1;
    this.history = null;
    this.load();
  }

  protected showShifts(): void {
    if (this.loading) return;
    this.selectedShift = null;
    this.page = 1;
    this.history = null;
    this.load();
  }

  protected toggleDetails(item: HistoryItem): void {
    if (!item.shift && item.operationId) {
      this.sidebar.openSidebar<boolean>(
        { type: SidebarType.VchasnoKasaOperationDetail, width: '680px' },
        { operationId: item.operationId, registerName: this.registerName },
      )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((changed) => { if (changed) this.load(); });
      return;
    }
    if (this.expanded.has(item)) this.expanded.delete(item);
    else this.expanded.add(item);
  }

}
